import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { Formik, Form } from 'formik';
import Yup from '../../utils/yup';
import { Box } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';

import useUsers from '../../context/UsersContext';
import { useLoader } from '../../provider/LoaderProvider';
import masterDataService from '../../service/masterData.service';
import simpletaskService from '../../service/simpletask.service';
import { NotificationType } from '../../config';
import addNotification from '../../utils/addNotification';

import { FileUploadButtonUI, FormButtonPanelUI } from '../Interface';
import useMenus from '../../context/MenuContext';
import dayjs from 'dayjs';
import { generateAutocompleteItems } from '../../utils/helper';
import SimpleTaskPhoneTabs from './SimpleTaskPhoneTabs';

export const SimpleTaskPhoneForm = ({ dialog }) => {
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const { getUser, user } = useUsers();
  const { showLoader, hideLoader } = useLoader();
  const [submenu, setSubmenu] = useState(null);

  const [defaultValues, setDefaultValues] = useState({});
  const [autocompleteData, setAutocompleteData] = useState({});
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    priority: [],
    project: [],
  });
  const [selectedValues, setSelectedValues] = useState({});
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [initValues, setInitValues] = useState({});

  useEffect(() => {
    getMenus();
    if (!Object.keys(user).length) getUser();

    const def = {
      simpleTaskName: '',
      priorId: null,
      startDate: dayjs(new Date()),
      endDate: dayjs(new Date()).add(1, 'hour'),
      duration: 1,
      resposiblePersonId: null,
      simpleTaskRespPersonlist: [],
      projectId: null,
      simpleTaskDescription: '',
      simpleTaskAttachment: [],
    };

    setDefaultValues(def);
  }, []);

  useEffect(() => {
    setSubmenu(getMenuSubMenuId('simpleTask'));
  }, [menus]);

  useEffect(() => {
    if (submenu) initDefaultValues();
  }, [submenu]);

  useEffect(() => {
    if (dialog?.dialogData && Object.keys(dialog.dialogData).length > 0) {
      setDefaultValues((prev) => {
        return { ...prev, ...dialog.dialogData };
      });
    }
  }, [dialog?.dialogData]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          priorId: autocompleteData?.priority?.defaultValue ?? null,
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          priority: autocompleteData?.priority?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
      } else {
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  const initDefaultValues = () => {
    showLoader();
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .typeToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .statusToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .priorityToForm(submenu)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          type: resp[0],
          status: resp[1],
          priority: resp[2],
          user: resp[3],
          project: resp[4],
        };
        setAutocompleteData(aData);

        const filteredProject = aData.project.filter((p) => p.active);

        const acOptions = {
          priority: generateAutocompleteItems(aData.priority.priorityListItems, 'itemName', 'itemId'),
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          project: generateAutocompleteItems(filteredProject, ['projectName', 'orderNumber'], 'projectId'),
        };

        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const validationSchema = Yup.object().shape({
    simpleTaskName: Yup.string().required(),
    priorId: Yup.string().required(),
    endDate: Yup.string().required(),
    resposiblePersonId: Yup.string().nullable(),
    projectId: Yup.string().nullable(),
    simpleTaskDescription: Yup.string().nullable().max(3000),
  });

  const valuesToForm = () => {
    const {
      simpleTaskName,
      priorId,
      endDate,
      resposiblePersonId,
      projectId,
      simpleTaskDescription,
      simpleTaskAttachment,
    } = selectedValues;

    setInitValues({
      simpleTaskName,
      priorId,
      endDate: dayjs(endDate),
      resposiblePersonId,
      projectId,
      simpleTaskDescription,
      simpleTaskAttachment,
    });
  };

  const handleInsertObject = (values, { resetForm }) => {
    showLoader();
    const insertObject = {
      ...values,
      userId: user.userId,
      createdBy: user.userId,
      statusId: autocompleteData.status?.defaultValue,
      simpleTaskTypeId: autocompleteData.type?.defaultValue,
      startDate: dayjs(values.startDate).toISOString(),
      endDate: dayjs(values.endDate).toISOString(),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.simpleTaskAttachment) {
      for (const file of insertObject.simpleTaskAttachment) {
        formData.append('files', file);
      }
    }

    simpletaskService
      .createSimpleTask(formData)
      .then(() => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm();

        if (dialog) {
          dialog.handleClose();
          dialog.setTaskChanged(true);
        }
      })

      .finally(() => {
        hideLoader();
      });
  };

  return (
    <div className={`${dialog ? '' : 'bg-gray-100 h-full w-full fixed top-0'}`}>
      <div className={`${dialog ? '' : 'h-auto bg-white py-5 px-5 w-96 mx-auto rounded-lg md:w-2/3 mt-32'}`}>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleInsertObject}
          enableReinitialize
          //validateOnMount={false}
        >
          {({ values, setFieldValue }) => {
            const handleUploadFile = (files) => {
              const filesWithId = files.map((file) => {
                file.id = uuidv4();
                return file;
              });

              const existingAttachments = Array.isArray(values.simpleTaskAttachment) ? values.simpleTaskAttachment : [];

              setFieldValue('simpleTaskAttachment', [...existingAttachments, ...filesWithId]);
            };

            return (
              <Form>
                <SimpleTaskPhoneTabs
                  selectedValues={selectedValues}
                  autoCompleteOptions={autoCompleteOptions}
                  handleUploadFile={handleUploadFile}
                />
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={3}
                  alignItems="center"
                  position="sticky"
                  bottom={0}
                  backgroundColor="#fff"
                  zIndex={10}
                >
                  <Box>
                    <FormButtonPanelUI />
                  </Box>
                  <Box>
                    <FileUploadButtonUI handleUploadFile={handleUploadFile} />
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};
